/**
 * First-touch acquisition cookie (BDAY-195, docs/metrics/v2-measurement-spec.md §6) — the
 * contract between the web client that writes it on a visitor's first visit and the server
 * that stamps it onto a new account at signup. Legacy kept the same thing as
 * `member.marketing_id` (utm_campaign / referrer label / "Birthday Viral Loop").
 *
 * Value: base64url(JSON) of {@link FirstTouch}. No PII: query tags, the referring HOST only,
 * the landing path, ad click ids. Never overwritten once set; 90 days.
 */
export const FIRST_TOUCH_COOKIE = "bday_ft";
export const FIRST_TOUCH_MAX_AGE_S = 90 * 24 * 60 * 60;

export interface FirstTouch {
  /** Landing path (no query), e.g. "/card/read/abc123". */
  p: string;
  /** Referring host when it was another site ("www.google.com"); absent = none / internal. */
  r?: string;
  /** utm_source / utm_medium / utm_campaign / utm_content / utm_term. */
  us?: string;
  um?: string;
  uc?: string;
  uct?: string;
  ut?: string;
  /** Meta click id, already in Meta's `fbc` format: `fb.1.<ms>.<fbclid>`. */
  fbc?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  /** First-visit time, epoch ms. */
  ts: number;
}

const MAX = 200;
const clip = (v: unknown): string | undefined =>
  typeof v === "string" && v.trim() ? v.trim().slice(0, MAX) : undefined;

/** Parse + sanitise a cookie value (anything malformed → null). */
export function decodeFirstTouch(raw: string | null | undefined): FirstTouch | null {
  if (!raw || raw.length > 4000) return null;
  try {
    const b64 = raw.replace(/-/g, "+").replace(/_/g, "/");
    const json =
      typeof atob === "function"
        ? decodeURIComponent(escape(atob(b64)))
        : Buffer.from(b64, "base64").toString("utf8");
    const o = JSON.parse(json) as Record<string, unknown>;
    const p = clip(o.p);
    const ts = typeof o.ts === "number" && Number.isFinite(o.ts) ? o.ts : null;
    if (!p || !p.startsWith("/") || ts === null) return null;
    return {
      p,
      r: clip(o.r),
      us: clip(o.us),
      um: clip(o.um),
      uc: clip(o.uc),
      uct: clip(o.uct),
      ut: clip(o.ut),
      fbc: clip(o.fbc),
      gclid: clip(o.gclid),
      gbraid: clip(o.gbraid),
      wbraid: clip(o.wbraid),
      ts,
    };
  } catch {
    return null;
  }
}

/** Encode for the cookie (base64url, no padding). */
export function encodeFirstTouch(ft: FirstTouch): string {
  const json = JSON.stringify(ft);
  const b64 =
    typeof btoa === "function"
      ? btoa(unescape(encodeURIComponent(json)))
      : Buffer.from(json, "utf8").toString("base64");
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Legacy's search-engine list (bday/web.py referrer_label): these hosts are `organic`. */
const ORGANIC_HOSTS = new Set([
  "google.com",
  "www.google.com",
  "www.aol.com",
  "aol.com",
  "www.ask.com",
  "ask.com",
  "www.bing.com",
  "bing.com",
  "www.daum.net",
  "daum.net",
  "duckduckgo.com",
  "ecosia.org",
  "www.ecosia.org",
  "www.lycos.com",
  "www.msn.com",
  "www.yahoo.com",
  "m.yahoo.com",
  "yahoo.com",
  "yandex.ru",
  "www.yandex.com",
]);

/** Legacy's referrer label: `organic` for a search engine, else `referral`; no referrer → `direct`. */
export function referrerLabel(host: string | null | undefined): "organic" | "referral" | "direct" {
  if (!host) return "direct";
  const h = host.toLowerCase();
  // Country Google domains (www.google.co.uk, …) were organic in practice too.
  if (ORGANIC_HOSTS.has(h) || /^(www\.)?google\.[a-z.]+$/.test(h)) return "organic";
  return "referral";
}
